import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/lib/auth-context";
import { deleteMyAccount, exportMyData } from "@/lib/privacy.functions";

const VISIBILITY = [
  { value: "public", label: "Public", hint: "Anyone, including people who are not signed in, can view your profile." },
  { value: "community", label: "Members only", hint: "Only signed-in Xamseen members can view your profile." },
  { value: "private", label: "Private", hint: "Your profile is hidden from search and member discovery." },
];

const PRECISION = [
  { value: "exact", label: "Exact", hint: "Street-level. Best for reports that depend on a precise spot." },
  { value: "approximate", label: "Approximate (~1 km)", hint: "Coordinates are rounded to the neighbourhood before saving." },
  { value: "city", label: "City only (~10 km)", hint: "Only the settlement is recoverable from what is stored." },
  { value: "hidden", label: "Do not store", hint: "No coordinates are saved with your reports at all." },
];

/**
 * Privacy controls and the two rights members can exercise without asking us:
 * data portability (export) and erasure (delete). Precision and visibility are
 * enforced server-side — precision by a database trigger at write time.
 */
export function PrivacyControls({
  visibility,
  precision,
  onChange,
}: {
  visibility: string;
  precision: string;
  onChange: (patch: { privacy_profile?: string; privacy_location?: string }) => void;
}) {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const visibilityHint = VISIBILITY.find((v) => v.value === visibility)?.hint;
  const precisionHint = PRECISION.find((p) => p.value === precision)?.hint;

  const handleExport = async () => {
    setExporting(true);
    try {
      const data = await exportMyData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `xamseen-my-data-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Your data export has been downloaded");
    } catch (e: any) {
      toast.error(e?.message ?? "Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteMyAccount();
      toast.success("Your account and content have been deleted");
      await signOut();
      void navigate({ to: "/" });
    } catch (e: any) {
      toast.error(e?.message ?? "Deletion failed. Please contact us so we can do it for you.");
      setDeleting(false);
    }
  };

  return (
    <Card className="space-y-6 p-6">
      <div>
        <h2 className="font-semibold">Privacy &amp; your data</h2>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          These settings are enforced when data is saved, not just when it is displayed.{" "}
          <Link to="/data-use" className="text-plum hover:underline">
            How Xamseen uses data
          </Link>
        </p>
      </div>

      <div>
        <Label>Profile visibility</Label>
        <Select value={visibility} onValueChange={(v) => onChange({ privacy_profile: v })}>
          <SelectTrigger className="mt-1.5">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {VISIBILITY.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="mt-1.5 text-xs text-muted-foreground">{visibilityHint}</p>
      </div>

      <div>
        <Label>Location precision on new reports</Label>
        <Select value={precision} onValueChange={(v) => onChange({ privacy_location: v })}>
          <SelectTrigger className="mt-1.5">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PRECISION.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
          {precisionHint} Reduced coordinates are what get stored, so precision cannot be recovered later. This applies to
          new reports; existing reports keep the precision they were saved with.
        </p>
      </div>

      <div className="space-y-3 border-t pt-5">
        <div>
          <h3 className="text-sm font-semibold">Download my data</h3>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            A JSON file containing your profile, posts, reports, comments, votes, confirmations, bookmarks, follows and
            preferences.
          </p>
        </div>
        <Button variant="outline" onClick={handleExport} disabled={exporting}>
          {exporting ? "Preparing your file…" : "Download my data"}
        </Button>
      </div>

      <div className="space-y-3 rounded-xl border border-ember/40 bg-ember/[0.05] p-4">
        <div>
          <h3 className="text-sm font-semibold text-ember">Delete my account</h3>
          <p className="mt-1 text-xs leading-relaxed text-foreground/80">
            Permanently deletes your account, profile and your content. This cannot be undone. Where a report has become
            part of a shared community record of an issue, a de-identified version may remain. Consider downloading your
            data first.
          </p>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" disabled={deleting}>
              {deleting ? "Deleting…" : "Delete my account"}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete your Xamseen account?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently removes your account, profile, posts, reports, comments and preferences. It cannot be
                undone, and we cannot restore it afterwards.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div>
              <Label htmlFor="confirm-delete" className="text-sm">
                Type DELETE to confirm
              </Label>
              <Input
                id="confirm-delete"
                className="mt-1.5"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                autoComplete="off"
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setConfirmText("")}>Keep my account</AlertDialogCancel>
              <AlertDialogAction
                disabled={confirmText.trim().toUpperCase() !== "DELETE" || deleting}
                onClick={handleDelete}
              >
                Delete permanently
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Card>
  );
}
